import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { BaseService } from "./base.service";
import { AccountRole } from "../enums/accountRole";
import { SigningAgentDesignation } from "../enums/signingAgentDesignation";
import { ClientAccountsUserInfo } from "../models/account";
import { SigningAgentDesignationAccount } from "../models/signing-agent-designation-account.model";
import { SigningAgentDesignationByEmail } from "../models/signing-agent-designation-by-email";
import {
  SigningAgentDesignationGetListWire,
  SigningAgentDesignationSummaryWire,
} from "../models/signing-agent-designation.api-contract";

export function toDesignationAccounts(
  response: SigningAgentDesignationGetListWire
): SigningAgentDesignationAccount[] {
  return (response?.signingAgentDesignations ?? []).map(toDesignationAccount);
}

function toDesignationAccount(
  wire: SigningAgentDesignationSummaryWire
): SigningAgentDesignationAccount {
  return {
    accountId: wire.accountId,
    firstName: wire.accountFirstName,
    lastName: wire.accountLastName,
    email: wire.accountEmailAddress,
    designationCode: wire.designationCode ?? null,
  };
}

@Injectable({
  providedIn: "root"
})
export class SigningAgentDesignationService extends BaseService {
  className = "SigningAgentDesignationService";

  constructor(injector: Injector) {
    super(injector);
  }

  getAllDesignationAccounts(): Observable<SigningAgentDesignationAccount[]> {
    return this.get<SigningAgentDesignationGetListWire>("/v1/signingAgentDesignations", [])
      .pipe(map(toDesignationAccounts));
  }

  putDesignationByAccountId(
    accountId: number,
    designationCode: SigningAgentDesignation
  ): Observable<object> {
    return this.put<object>(
      `/v1/accounts/${accountId}/signingAgentDesignation/${designationCode}`,
      null
    );
  }

  deleteDesignationByAccountId(accountId: number): Observable<object> {
    return this.delete(`/v1/accounts/${accountId}/signingAgentDesignation`);
  }

  putDesignationByEmail(model: SigningAgentDesignationByEmail): Observable<object> {
    const emailAddress = (model.emailAddress ?? "").trim();

    return this.findSigningAgentAccountId(emailAddress).pipe(
      switchMap(accountId => this.putDesignationByAccountId(accountId, model.designationCode))
    );
  }

  private findSigningAgentAccountId(emailAddress: string): Observable<number> {
    const searchAddress = encodeURIComponent(emailAddress.toUpperCase());

    return this.getWithoutErrorHandling<ClientAccountsUserInfo>(
      `/v1/clientaccounts/emailaddress?emailAddress=${searchAddress}`
    ).pipe(
      catchError((error: HttpErrorResponse) =>
        throwError(() => new Error(
          error?.status === 404
            ? `No account found for ${emailAddress}.`
            : `Unable to look up ${emailAddress} right now. Please try again.`
        ))
      ),
      map(account => {
        if (!account?.accountId) {
          throw new Error(`No account found for ${emailAddress}.`);
        }

        const isSigningAgent = (account.clientAccounts ?? []).some(
          clientAccount =>
            clientAccount.accountType?.toUpperCase() === AccountRole.SigningAgent
        );

        if (!isSigningAgent) {
          throw new Error(`${emailAddress} does not hold the Signing Agent role.`);
        }

        return account.accountId;
      })
    );
  }
}
